import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import { normalizeMesReferencia, brl, dataBr } from './format';
import { statusAcordoBadge, statusParcelaBadge } from './statusParcela';
import {
    Loader2,
    ArrowLeft,
    CalendarClock,
    Headset,
    CheckCircle2,
    AlertCircle,
} from 'lucide-react';
import PortalConsumidorHeader from './PortalConsumidorHeader';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';

// Portal do Consumidor — Meus Parcelamentos (rota /portal-consumidor/parcelamentos).
// Versão read-only do ParcelamentosView interno: acordo (valor total, nº de parcelas,
// status, progresso) + cronograma de parcelas. A RLS restritiva
// (migration 20260710100000) garante que o consumidor só recebe acordos das UCs
// vinculadas ao CPF dele — a query é um select simples.

interface ParcelaPortal {
    id: string;
    numero_parcela: number;
    valor: number | null;
    mes_referencia: string | null;
    data_vencimento: string | null;
    status: 'pendente' | 'provisionado' | 'pago' | 'cancelado' | string;
}

interface ParcelamentoPortal {
    id: string;
    descricao: string | null;
    valor_total: number | null;
    numero_parcelas: number;
    status: 'ativo' | 'concluido' | 'cancelado' | string;
    created_at: string;
    parcelamento_parcelas: ParcelaPortal[];
}

const PortalConsumidorParcelamentos = () => {
    const { user, profile, loading, signOut } = useAuth();

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [parcelamentos, setParcelamentos] = useState<ParcelamentoPortal[]>([]);

    const { marca } = useMarcaPortal();
    const EMAIL_SUPORTE = marca.contato.email;

    useTituloPortal('Meus Parcelamentos');

    useEffect(() => {
        if (!user || profile?.role !== 'consumidor') return;
        const carregar = async () => {
            setCarregando(true);
            setErro(null);
            try {
                const { data, error } = await supabase
                    .from('parcelamentos')
                    .select('id, descricao, valor_total, numero_parcelas, status, created_at, parcelamento_parcelas(id, numero_parcela, valor, mes_referencia, data_vencimento, status)')
                    .order('created_at', { ascending: false });
                if (error) throw error;
                setParcelamentos((data || []) as ParcelamentoPortal[]);
            } catch (err) {
                console.error('Erro ao carregar parcelamentos do portal:', err);
                setErro('Não foi possível carregar seus parcelamentos. Tente novamente em instantes.');
            } finally {
                setCarregando(false);
            }
        };
        carregar();
    }, [user, profile]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalConsumidorHeader />

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                <div className="flex items-center gap-3">
                    <Link
                        to="/portal-consumidor/inicio"
                        className="p-2 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Meus Parcelamentos</h1>
                        <p className="text-sm text-gray-500">Acordos de renegociação e o cronograma das parcelas.</p>
                    </div>
                </div>

                {erro && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm">
                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                        <span>{erro}</span>
                    </div>
                )}

                {carregando ? (
                    <div className="flex justify-center py-20">
                        <Loader2 size={32} className="animate-spin text-primary" />
                    </div>
                ) : parcelamentos.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center space-y-4">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                            <CalendarClock size={30} className="text-primary" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900">Nenhum parcelamento</h2>
                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                            Você não possui acordos de parcelamento no momento. Se precisar renegociar algum débito,
                            fale com o nosso atendimento.
                        </p>
                        <a
                            href={`mailto:${EMAIL_SUPORTE}?subject=${encodeURIComponent('Renegociação de débitos — Portal do Consumidor')}`}
                            className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
                        >
                            <Headset size={16} />
                            Falar com o suporte
                        </a>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {parcelamentos.map((acordo) => {
                            const parcelas = [...(acordo.parcelamento_parcelas || [])].sort((a, b) => a.numero_parcela - b.numero_parcela);
                            const pagas = parcelas.filter((p) => p.status === 'pago').length;
                            const total = acordo.numero_parcelas || parcelas.length || 1;
                            const progresso = Math.min(100, Math.round((pagas / total) * 100));
                            const badgeAcordo = statusAcordoBadge(acordo.status);

                            return (
                                <div key={acordo.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h2 className="font-bold text-gray-900 text-lg">
                                                        {acordo.descricao || 'Parcelamento'}
                                                    </h2>
                                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeAcordo.classe}`}>
                                                        {badgeAcordo.rotulo}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Criado em {dataBr(acordo.created_at)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs font-medium text-gray-400 uppercase">Valor total</p>
                                                <p className="text-2xl font-bold text-gray-900">{brl(acordo.valor_total)}</p>
                                                <p className="text-xs text-gray-400">{total}x</p>
                                            </div>
                                        </div>

                                        {/* Progresso */}
                                        <div className="mt-5">
                                            <div className="flex items-center justify-between text-xs font-semibold mb-1.5">
                                                <span className="text-gray-500">
                                                    {pagas} de {total} parcelas pagas
                                                </span>
                                                <span className="text-primary">{progresso}%</span>
                                            </div>
                                            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-primary rounded-full transition-all"
                                                    style={{ width: `${progresso}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Cronograma */}
                                    {parcelas.length > 0 && (
                                        <div className="border-t border-gray-100 divide-y divide-gray-50">
                                            {parcelas.map((parcela) => {
                                                const badge = statusParcelaBadge(parcela.status, parcela.data_vencimento);
                                                return (
                                                    <div key={parcela.id} className="px-6 py-3.5 flex items-center gap-4">
                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-xs font-bold ${parcela.status === 'pago' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                            {parcela.status === 'pago' ? <CheckCircle2 size={17} /> : `${parcela.numero_parcela}º`}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                Parcela {parcela.numero_parcela} de {total}
                                                                {parcela.mes_referencia && (
                                                                    <span className="text-gray-400 font-normal"> · {normalizeMesReferencia(parcela.mes_referencia)}</span>
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-gray-400">Vencimento {dataBr(parcela.data_vencimento)}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-sm font-bold text-gray-900">{brl(parcela.valor)}</p>
                                                            <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mt-0.5 ${badge.classe}`}>
                                                                {badge.rotulo}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default PortalConsumidorParcelamentos;